import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import useAssets from "../../../../hooks/useAssets";
import useScheduling from "../../../../hooks/useScheduling";
import { Loading } from "../../../../utils/loading";
import { Asset } from "../../../../models/asset";
import { getAssetRioPlan } from "../../../../services/assetService";
import { useMediaQuery } from "@mui/material";
import VehicleIcon from "../../../../components/VehicleIcon";

const Details = (props: any) => {
    const { id } = useParams();
    const intl = useIntl();
    const t = (key: string) => intl.formatMessage({ id: `intl-msg:appointment.step4.${key}` });

    const { loading, scheduling } = useScheduling(id);

    const { allAssets } = useAssets();
    const [asset, setAsset] = useState<Asset | undefined>(undefined);

    useEffect(() => {
        if(scheduling) {
            const asset = allAssets.find(asset => asset.id === scheduling.asset?.id);
            if(asset) setAsset(asset);
        }
    }, [scheduling]);   

    const { plans } = getAssetRioPlan(scheduling?.asset?.id ?? '');
    const isResponsiveView = useMediaQuery("(max-width: 767px)");

    return (
        <>
            {loading ?
                <Loading label={intl.formatMessage({ id: "intl-msg:controlTower.details.loading" })} /> :
                <div className={`scheduling-page resume-section ${isResponsiveView ? "p-4" : "p-10"}`}>
                    <h2 className="text-size-h2 text-center text-color-black font-semibold padding-top-20 padding-bottom-20">
                        {t("title")}
                    </h2>
                    <div className="resume-container">
                        <h5 className="text-size-20 text-color-black font-bold margin-bottom-20 text-color-gray">
                            {scheduling?.scheduleNumber}
                        </h5>


                        <div className={`row schedule-data flex ${isResponsiveView ? "flex-col gap-3" : ""}`}>
                            {[
                                { label: t("driver"), value: scheduling?.driver?.name, width: "col-4" },
                                { label: t("license"), value: scheduling?.driver?.license, width: "col-2" },
                                { label: t("phone"), value: scheduling?.driver?.phone, width: "col-3" },
                                { label: t("email"), value: scheduling?.driver?.email, width: "col-3" },
                            ].map((field, index) => (
                                <div key={index} className={`${isResponsiveView ? "w-full mb-6" : field.width}`}>
                                    <label className="block text-sm text-size-20 text-color-black font-bold mb-2">
                                        {field.label}
                                    </label>
                                    <div className="mt-1 p-2 border schedule-data__info">
                                        <div className="truncate">{field.value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="p-4 bg-lighter margin-top-20 vehicle-detais">
                            <div className="flex items-start gap-4">
                                <VehicleIcon 
                                    chassis={asset?.chassis ?? scheduling?.asset?.chassis} 
                                    size={80}
                                />

                                <div className="flex-1 padding-left-10">
                                    <p className="text-color-black text-size-16 font-medium m-0">
                                        {asset?.name ?? scheduling?.asset?.name} | {t("plateLabel")}{" "}
                                        {asset?.licensePlate ?? scheduling?.asset?.licensePlate}
                                    </p>
                                    <p className="text-color-status-resting text-size-14 m-0">
                                        {t("chassisLabel")} {asset?.chassis ?? scheduling?.asset?.chassis}
                                        <br />
                                        {t("odometerLabel")} {scheduling?.asset?.odometer} km | {t("hourmeterLabel")}{" "}
                                        {scheduling?.asset?.hourmeter}
                                    </p>
                                </div>

                                {(scheduling?.asset?.plans?.length ?? 0) > 0 && (
                                    <div className="flex gap-6 margin-bottom-20">
                                        <span className="px-2 py-1 green-tag">RIO</span>
                                        <span className="px-2 py-1 green-tag">{t("inWarranty")}</span>
                                        {scheduling?.asset?.volksTotalPlan && (
                                            <span className="px-2 py-1 bg-white dark:bg-neutral-800 white-tag">
                                                Volkstotal {scheduling?.asset?.volksTotalPlan}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="padding-top-10">
                                <div className="car-workshop">
                                    <ul className="flex flex-column gap-5 m-0">
                                        <li className="flex justify-between">
                                            <div className="text-color-black">{t("fieldCampaigns")}</div>
                                            <div className="text-color-black font-bold">{scheduling?.asset?.id ? plans : t("free")}</div>
                                        </li>

                                        <li className="flex justify-between">
                                            <div className="text-color-black">
                                                {scheduling?.checkup?.description}
                                            </div>
                                            <div className="text-color-black font-bold">{scheduling?.asset?.id ? plans : t("free")}</div>
                                        </li>

                                        <li className="flex justify-between">
                                            <div className="text-color-black">{t("totalServices")}</div>
                                            <div className="text-color-black">
                                                {scheduling?.totalPrice ?? "R$ 0,00"}
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="revision-itens">
                            <h5 className="text-size-20 text-color-black font-bold margin-bottom-10">
                                {t("coveredItems")}
                            </h5>
                            <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700">
                                <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
                                    {(scheduling?.checkup?.parts ?? []).length === 0 && <p>-</p>}
                                    {(scheduling?.checkup?.parts ?? []).map((part, index) => (
                                        <div key={index} className="padding-bottom-10 flex justify-between">
                                            <p className="m-0">
                                                {part.description}
                                                {part.qtd ? ` (${part.qtd})` : ""}
                                            </p>
                                            {!part.isCovered && part.notCoveredReason && (
                                                <p className="m-0 text-color-status-resting">{part.notCoveredReason}</p>
                                            )}
                                        </div>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="revision-itens">
                            <h5 className="text-size-20 text-bold margin-bottom-0">
                                {t("contractedServicesTitle")}
                            </h5>
                            <div className="text-size-14 margin-bottom-10">
                                {t("contractedServicesDesc")}
                            </div>
                            <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700">
                                <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
                                    {(scheduling?.otherServices ?? []).map((name, index) => (
                                        <div key={index}>
                                            <p>{name}</p>
                                        </div>
                                    ))}
                                    {(scheduling?.otherServices ?? []).length === 0 && <p>-</p>}
                                </ul>
                            </div>
                        </div>

                        <div className="revision-itens observation">
                            <h5 className="text-size-20 text-color-black font-bold margin-bottom-10">
                                {t("observations")}
                            </h5>
                            <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700">
                                <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
                                    <li className="flex justify-between">
                                        <span>{scheduling?.observation || '-'}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div
                            className={`revision-itens flex ${isResponsiveView ? "flex-col gap-4" : "justify-between gap-6"
                                } mt-4`}
                        >
                            {[
                                {
                                    title: t("dealershipTitle"),
                                    content: (
                                        <>
                                            <p className="m-0 text-size-h5">
                                                {scheduling?.dealership?.name}
                                            </p>
                                            <p className="m-0">{scheduling?.dealership?.address}</p>
                                        </>
                                    ),
                                },
                                {
                                    title: t("dateAndTime"),
                                    content: (
                                        <>
                                            <p className="m-0 text-size-h5">
                                                {scheduling?.date.toLocaleDateString(intl.locale, {
                                                    year: "numeric",
                                                    month: "long",
                                                    day: "2-digit",
                                                })}
                                            </p>
                                            <p className="m-0">
                                                {scheduling?.date.toLocaleTimeString(intl.locale, {
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                            </p>
                                        </>
                                    ),
                                },
                                {
                                    title: t("consultant"),
                                    content: <p className="m-0 text-size-h5">{scheduling?.consultant}</p>,
                                },
                            ].map((item, idx) => (
                                <div
                                    key={idx}
                                    className={`${isResponsiveView ? "w-full mb-10" : "w-1/3"}`}
                                >
                                    <h5 className="text-size-20 text-color-black font-bold mb-2">
                                        {item.title}
                                    </h5>
                                    <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700 card">{item.content}</div>
                                </div>
                            ))}
                        </div>

                        <div className="warning-row">
                            <h5 className="text-color-status-resting">{t("attention")}</h5>
                            <p className="text-color-warning text-size-18 font-semibold">
                                {t("warrantyWarning")}
                            </p>
                        </div>
                        <div className="flex justify-center navigation-buttons step-4">
                            <button className="back-btn" onClick={() => window.history.back()}>
                                {t("back")}
                            </button>
                        </div>
                    </div>
                </div>
            }
        </>
    );
}

export default Details;